import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import { toWidget, toWidgetEditable } from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';

export default class AccordionEditing extends Plugin {
    static get requires() {
        return [Widget];
    }

    init() {
        console.log('AccordionEditing#init() called');

        const DEFAULT_TEXTS = {
            header: '',
            body: '',
            newHeader: '',
            newBody: ''
        };

        this._defineSchema();
        this._defineConverters();

        const createAccordion = (writer, headerText, bodyText) => {
            const accordion = writer.createElement('accordion');
            const header = writer.createElement('accordionHeader');
            const body = writer.createElement('accordionBody');

            writer.append(header, accordion);
            writer.append(body, accordion);

            writer.insertText(headerText, header);

            const bodyParagraph = writer.createElement('paragraph');
            writer.insertText(bodyText, bodyParagraph);
            writer.append(bodyParagraph, body);

            return accordion;
        };

        // Command to insert an accordion
        this.editor.commands.add('insertAccordion', {
            execute: () => {
                const model = this.editor.model;
                const selection = model.document.selection;

                model.change(writer => {
                    const accordion = createAccordion(writer, DEFAULT_TEXTS.header, DEFAULT_TEXTS.body);
                    model.insertContent(accordion, selection.getFirstPosition());
                });
            }
        });

        // Event listener for Tab key in the last accordionBody
        this.editor.editing.view.document.on('keydown', (evt, data) => {
            if (data.keyCode === 9) { // Tab key
                const viewElement = data.target;
                const modelElement = this.editor.editing.mapper.toModelElement(viewElement);

                if (modelElement?.is('element', 'accordionBody') && !modelElement.parent.nextSibling) {
                    const model = this.editor.model;

                    model.change(writer => {
                        const newAccordion = createAccordion(writer, DEFAULT_TEXTS.newHeader, DEFAULT_TEXTS.newBody);
                        model.insertContent(newAccordion, writer.createPositionAfter(modelElement.parent));
                        writer.setSelection(newAccordion.getChild(0), 'in'); // Focus new header
                    });

                    data.preventDefault();
                }
            }
        });
    }

    _defineSchema() {
        const schema = this.editor.model.schema;

        schema.register('accordion', {
            isObject: true,
            allowWhere: '$block',
            allowChildren: ['accordionHeader', 'accordionBody']
        });

        schema.register('accordionHeader', {
            isLimit: true,
            allowIn: 'accordion',
            allowContentOf: '$block'
        });

        schema.register('accordionBody', {
            isLimit: true,
            allowIn: 'accordion',
            allowContentOf: '$root'
        });
    }

    _defineConverters() {
        const conversion = this.editor.conversion;

        // Accordion
        conversion.for('upcast').elementToElement({
            model: 'accordion',
            view: {
                name: 'div',
                classes: 'accordion'
            }
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'accordion',
            view: {
                name: 'div',
                classes: 'accordion'
            }
        });
        conversion.for('editingDowncast').elementToElement({
            model: 'accordion',
            view: (modelElement, { writer }) => {
                const div = writer.createContainerElement('div', { class: 'accordion' });
                return toWidget(div, writer, { label: 'accordion widget' });
            }
        });

        // Accordion Header
        conversion.for('upcast').elementToElement({
            model: 'accordionHeader',
            view: {
                name: 'h3',
                classes: 'accordion-header'
            }
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'accordionHeader',
            view: {
                name: 'h3',
                classes: 'accordion-header'
            }
        });
        conversion.for('editingDowncast').elementToElement({
            model: 'accordionHeader',
            view: (modelElement, { writer }) => {
                const h3 = writer.createEditableElement('h3', { class: 'accordion-header' });
                return toWidgetEditable(h3, writer);
            }
        });

        // Accordion Body
        conversion.for('upcast').elementToElement({
            model: 'accordionBody',
            view: {
                name: 'div',
                classes: 'accordion-body'
            }
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'accordionBody',
            view: {
                name: 'div',
                classes: 'accordion-body'
            }
        });
        conversion.for('editingDowncast').elementToElement({
            model: 'accordionBody',
            view: (modelElement, { writer }) => {
                const div = writer.createEditableElement('div', { class: 'accordion-body' });
                return toWidgetEditable(div, writer);
            }
        });
    }
}
